import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { 
  LayoutDashboard, 
  School, 
  Users, 
  Calendar, 
  Clock, 
  UserCog, 
  FileDown,
  LogOut,
  Settings,
  GraduationCap,
  ChevronRight,
  AlertCircle,
  User,
  ScrollText,
  CreditCard,
  HelpCircle,
  ExternalLink,
  Menu,
  X,
  BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Toaster } from '../ui/sonner';

const DashboardLayout = ({ children }) => {
  const { user, logout, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await api.get('/auth/subscription-info');
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    }
  };

  const getDaysRemaining = () => {
    if (!subscriptionInfo?.license_expiry_date) return null;
    const expiry = new Date(subscriptionInfo.license_expiry_date);
    const now = new Date();
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  };

  const formatExpiryDate = () => {
    if (!subscriptionInfo?.license_expiry_date) return null;
    const date = new Date(subscriptionInfo.license_expiry_date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMakePayment = async () => {
    try {
      await api.post('/auth/payment-initiated');
    } catch (error) {
      // Continue anyway
    }
    window.open('https://wise.com/pay/r/frHEQtOCSPOj5RA', '_blank');
  };

  const isSystemAdmin = user?.role === 'system_admin';
  const isSchoolAdmin = user?.role === 'school_admin';
  const subscriptionExpired = user?.subscription_expired === true;
  const daysRemaining = getDaysRemaining();
  const showLicenseWarning = daysRemaining !== null && daysRemaining <= 14;

  const systemAdminLinks = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/admin/schools', icon: School, label: 'Schools' },
    { to: '/admin/subscriptions', icon: CreditCard, label: 'Subscriptions' },
    { to: '/admin/users', icon: Users, label: 'All Users' },
    { to: '/admin/changelog', icon: ScrollText, label: 'Changelog' },
    { to: '/admin/settings', icon: Settings, label: 'System Settings' },
  ];

  const schoolLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/teachers', icon: GraduationCap, label: 'Teachers' },
    { to: '/conference-sets', icon: Calendar, label: 'Conference Sets' },
    { to: '/schedule-sets', icon: Clock, label: 'Schedule Sets' },
    { to: '/exports', icon: FileDown, label: 'Export Sets' },
  ];

  const schoolAdminLinks = [
    { to: '/school-settings', icon: Settings, label: 'School Settings' },
    { to: '/manage-users', icon: UserCog, label: 'Manage Users' },
  ];

  // If subscription is expired for school admin, only show School Settings
  const expiredSchoolAdminLinks = [
    { to: '/school-settings', icon: Settings, label: 'School Settings' },
  ];

  // Determine which navigation links to show
  let navLinks;
  if (isSystemAdmin && !user?.impersonating) {
    navLinks = systemAdminLinks;
  } else if (subscriptionExpired && isSchoolAdmin) {
    // School admin with expired subscription - only show School Settings
    navLinks = expiredSchoolAdminLinks;
  } else {
    // Normal school user or admin
    navLinks = [...schoolLinks, ...(isSchoolAdmin || user?.impersonating ? schoolAdminLinks : [])];
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <h1 className="font-outfit text-lg font-bold">PTC Builder</h1>
        </div>
        <p className="text-slate-400 text-sm truncate max-w-[120px]">{user?.full_name || user?.username}</p>
      </div>

      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-slate-900 text-white flex flex-col z-50
        w-64 transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="p-6 border-b border-slate-700">
          <h1 className="font-outfit text-xl font-bold tracking-tight">PTC Builder</h1>
          <p className="text-slate-400 text-sm mt-1">Conference Management</p>
        </div>

        {/* Impersonation Banner */}
        {user?.impersonating && (
          <div className="mx-4 mt-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg">
            <div className="flex items-center gap-2 text-amber-300 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">Impersonating User</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 text-amber-300 hover:text-amber-100 hover:bg-amber-500/20 w-full justify-start p-2 text-xs"
              onClick={stopImpersonation}
              data-testid="stop-impersonation-btn"
            >
              Stop Impersonation
            </Button>
          </div>
        )}

        {/* Subscription Expired Banner for School Admin */}
        {subscriptionExpired && isSchoolAdmin && !user?.impersonating && (
          <div className="mx-4 mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
            <div className="flex items-center gap-2 text-red-300 text-sm font-medium">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Subscription Expired</span>
            </div>
            <p className="text-red-300/80 text-xs mt-1">
              Your access is limited. Please renew your subscription to continue using PTC Builder.
            </p>
            <button 
              onClick={handleMakePayment}
              className="mt-2 w-full flex items-center justify-center gap-1 text-xs font-medium py-1.5 px-3 rounded bg-red-500 text-white hover:bg-red-600 transition-colors"
              data-testid="expired-payment-btn"
            >
              <ExternalLink className="h-3 w-3" />
              Make Payment
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <link.icon className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium truncate">{link.label}</span>
              <ChevronRight className="h-4 w-4 ml-auto opacity-50 flex-shrink-0" />
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-700">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-white truncate">{user?.full_name || user?.username}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
          
          {/* License Expiry Indicator */}
          {subscriptionInfo && !subscriptionInfo.is_system_admin && subscriptionInfo.license_expiry_date && (
            <div className={`mx-4 mb-3 p-3 rounded-lg ${
              daysRemaining <= 0 ? 'bg-red-500/20 border border-red-500/50' :
              daysRemaining <= 7 ? 'bg-amber-500/20 border border-amber-500/50' :
              daysRemaining <= 14 ? 'bg-amber-500/10 border border-amber-500/30' :
              'bg-slate-800'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className={`h-4 w-4 flex-shrink-0 ${
                  daysRemaining <= 0 ? 'text-red-400' :
                  daysRemaining <= 7 ? 'text-amber-400' :
                  'text-slate-400'
                }`} />
                <span className={`text-xs font-medium truncate ${
                  daysRemaining <= 0 ? 'text-red-300' :
                  daysRemaining <= 7 ? 'text-amber-300' :
                  'text-slate-300'
                }`}>
                  {subscriptionInfo.subscription_status === 'trial' ? 'Trial' : 'License'} 
                  {daysRemaining <= 0 ? ' Expired' : ` Expires`}
                </span>
              </div>
              <p className={`text-xs ${
                daysRemaining <= 0 ? 'text-red-400' :
                daysRemaining <= 7 ? 'text-amber-400' :
                'text-slate-400'
              }`}>
                {daysRemaining <= 0 
                  ? 'Please renew' 
                  : `${formatExpiryDate()} (${daysRemaining}d)`}
              </p>
              {showLicenseWarning && isSchoolAdmin && (
                <button 
                  onClick={handleMakePayment}
                  className={`mt-2 w-full flex items-center justify-center gap-1 text-xs font-medium py-1.5 px-3 rounded ${
                    daysRemaining <= 7 
                      ? 'bg-amber-500 text-white hover:bg-amber-600' 
                      : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  } transition-colors`}
                  data-testid="payment-link"
                >
                  <ExternalLink className="h-3 w-3" />
                  Make Payment
                </button>
              )}
            </div>
          )}

          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg mb-1 transition-all duration-200 ${
                isActive
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`
            }
            data-testid="nav-profile"
          >
            <User className="h-4 w-4" />
            <span className="text-sm">My Profile</span>
          </NavLink>
          <NavLink
            to="/help"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-lg mb-1 transition-all duration-200 ${
                isActive
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`
            }
            data-testid="nav-help"
          >
            <HelpCircle className="h-4 w-4" />
            <span className="text-sm">Help</span>
          </NavLink>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 flex flex-col min-h-screen pt-14 lg:pt-0">
        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </div>
        
        {/* Footer */}
        <footer className="bg-slate-100 border-t border-slate-200 py-4 px-4 sm:px-8">
          <p className="text-center text-xs sm:text-sm text-slate-500">
            © {currentYear} Borrow A Geek Limited Trading as EDUx. All Rights Reserved. | <Link to="/changelog" className="text-indigo-600 hover:text-indigo-700 hover:underline" data-testid="version-link">Version 1.4</Link>
          </p>
        </footer>
      </main>

      <Toaster position="top-right" />
    </div>
  );
};

export default DashboardLayout;
